import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { styled, useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Chip, Container, CssBaseline, Grid, Typography } from '@mui/material';
import { letterSpacing } from '@mui/system';

import { useDispatch, useSelector } from 'store';
import { useStore } from 'react-redux';
import { clearStore, getCategories } from 'store/slices/postList';
import { clearPostInStore, getBlogPost } from 'store/slices/postDetail';

import { Helmet } from 'react-helmet-async';
import {
    buildSchemaGraph,
    generateWebPageSchema,
    generateBlogPostingSchema,
    generateBreadcrumbSchema,
    renderMultipleStructuredData
} from 'utils/structuredData';
import { GEO_OG_SITE_NAME, GEO_SITE_TAGLINE } from 'static/geoPageInfo';
import PageNotFound from 'views/page-not-found';
import { buildBlogDetailPath, buildBlogListPath } from 'utils/blogUrl';

import parse from 'html-react-parser';

// ==============================|| POST DETAIL PAGE ||============================== //

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

const contentTheme = createTheme({
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                h2, h3 {
                    color: #1C1C1C;
                    font-size: 1.875rem;
                    font-weight: 800;
                    font-family: 'Heebo Variable', Heebo, sans-serif;
                    line-height: 1.43;
                    margin-top: 0;
                    margin-bottom: 10px;
                }

                h4 {
                    color: #1C1C1C;
                    font-size: 1.875rem;
                    font-weight: 800;
                    font-family: 'Heebo Variable', Heebo, sans-serif;
                    line-height: 1.43;
                    margin-top: 0;
                    margin-bottom: 20px;
                }

                h5 {
                    color: #1C1C1C;
                    font-size: 1.5625rem;
                    font-weight: 800;
                    font-family: 'Heebo Variable', Heebo, sans-serif;
                    line-height: 1.43;
                    margin-top: 0;
                    margin-bottom: 4px;
                }

                p {
                    color: #1C1C1C;
                    font-size: 1.25rem;
                    font-weight: 400;
                    font-family: 'Raleway Variable', Raleway, sans-serif;
                    line-height: 1.43;
                    margin-top: 0;
                    margin-bottom: 10px;
                    word-break: break-word;
                    min-height: 24px;
                }

                ul {
                    margin: 0.5rem 0;
                    padding-left: 2rem;
                }

                li {
                    color: #1C1C1C;
                    font-size: 1.25rem;
                    font-weight: 400;
                    font-family: 'Raleway Variable', Raleway, sans-serif;
                    line-height: 1.43;
                    margin-top: 0;
                    margin-bottom: 20px;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1.5rem 0;
                }

                th,
                td {
                    border: 1px solid #1C1C1C;
                    padding: 0.75rem 1rem;
                    text-align: left;
                    font-family: 'Raleway Variable', Raleway, sans-serif;
                    font-size: 1rem;
                    color: #1C1C1C;
                }

                th {
                    background-color: #f5f5f5;
                    font-weight: 600;
                }

                iframe {
                    width: 1080px;
                    height: 607.5px;
                    display: block;
                    margin: 0 auto;
                }

                img {
                    max-width: 100%;
                }

                .quick-takeaways-content > h3 {
                    font-size: 16px !important;
                    line-height: 26px !important;
                    margin-bottom: 0 !important;
                }

                .quick-takeaways-content > h3 > * {
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    font-style: italic;
                    color: #1C1C1C !important;
                    line-height: 26px !important;
                }

                .quick-takeaways-content > p {
                    font-size: 16px !important;
                    line-height: 26px !important;
                    margin-bottom: 20px !important;
                }

                .quick-takeaways-content > p:last-child {
                    margin-bottom: 0px !important;
                }

                .quick-takeaways-content > p > * {
                    font-size: 16px !important;
                    font-weight: 500 !important;
                    font-style: italic;
                    color: #1C1C1C !important;
                    line-height: 26px !important;
                }

                .quick-takeaways-content strong {
                    font-weight: 600 !important;
                }

                .quick-takeaways-content > ul > li {
                    font-size: 16px !important;
                    font-weight: 500 !important;
                    line-height: 26px !important;
                    font-style: italic;
                    color: #1C1C1C !important;
                    margin-bottom: 20px !important;
                }

                .quick-takeaways-content > ul > li:last-child {
                    margin-bottom: 0px !important;
                }

                @media (max-width: 1200px) {
                    iframe {
                        width: 860px;
                        height: 472.5px;
                    }
                }

                @media (max-width: 900px) {
                    iframe {
                        width: 540px;
                        height: 303.75px;
                    }
                }

                @media (max-width: 959.95px) {
                    h3 {
                        font-size: 1.5rem;
                    }

                    h4 {
                        font-size: 1.5rem;
                        margin-bottom: 15px;
                    }

                    h5 {
                        font-size: 1.25rem;
                        margin-bottom: 2px;
                    }

                    p {
                        font-size: 1rem;
                        margin-bottom: 8px;
                    }

                    li {
                        font-size: 1rem;
                        margin-bottom: 1rem;
                    }

                    table {
                        display: block;
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                    }

                    th,
                    td {
                        white-space: nowrap;
                    }
                }

                @media (max-width: 600px) {
                    iframe {
                        width: 320px;
                        height: 180px;
                    }

                    img {
                        width: 100%;
                        height: auto;
                    }

                    .quick-takeaways-content > h3 {
                        font-size: 14px !important;
                        line-height: 20px !important;
                        margin-bottom: 0 !important;
                    }

                    .quick-takeaways-content > h3 > * {
                        font-size: 14px !important;
                        line-height: 20px !important;
                    }

                    .quick-takeaways-content > p {
                        font-size: 14px !important;
                        line-height: 20px !important;
                        margin-bottom: 16px !important;
                    }

                    .quick-takeaways-content > p:last-child {
                        margin-bottom: 0px !important;
                    }

                    .quick-takeaways-content > p > * {
                        font-size: 14px !important;
                        line-height: 20px !important;
                    }

                    .quick-takeaways-content > ul > li {
                        font-size: 14px !important;
                        line-height: 20px !important;
                        margin-bottom: 16px !important;
                    }
                }
            `
        }
    }
});

const PostDetailPage = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const { categoryList } = useSelector((state) => state.postList);
    const store = useStore(); // Get direct store access for SSR reliability
    const isServer = typeof window === 'undefined';

    // postDetail slice contains { error, postDetail: {...} }
    // We destructure to get the actual postDetail object
    // Always use useSelector for React hooks compliance
    const postDetailStateFromSelector = useSelector((state) => state.postDetail);

    // CRITICAL: On server, useSelector may not reflect the latest store state immediately
    // during SSR render. Use store.getState() directly as the source of truth for SSR.
    // On client, useSelector works correctly with React's update cycle.
    const postDetailState = isServer ? store.getState().postDetail : postDetailStateFromSelector;
    const { postDetail } = postDetailState || {};
    const errorStatus = postDetailState?.error?.status || postDetailState?.error?.response?.status;
    const hasRequestError = typeof errorStatus === 'number' && errorStatus >= 400;
    const isNotFoundFromStore = postDetailState?.notFound === true || errorStatus === 404;
    const hasRenderablePostData = Boolean(postDetail?.postTitle || postDetail?.postContent);

    // Validate ID from route params
    const isValidId = id && id !== 'null' && id !== 'undefined' && (typeof id === 'string' ? id.trim() !== '' : true);

    if (!isValidId && isServer && isDevelopment) {
        console.error('[SSR] PostDetailPage - CRITICAL: Invalid ID from useParams()');
        console.error('[SSR] PostDetailPage - ID value:', id, 'Type:', typeof id);
        console.error('[SSR] PostDetailPage - URL path:', location.pathname);
        console.error('[SSR] PostDetailPage - This indicates the URL is malformed (e.g., /blog/detail/null)');
        console.error('[SSR] PostDetailPage - Metadata will use fallback values - URL previews will not show post content');
    }
    // On server, log the full state to debug
    if (isServer && isDevelopment) {
        const storeState = store.getState().postDetail;
        console.log('[SSR] PostDetailPage - Store state comparison:', {
            fromSelector: {
                hasState: !!postDetailStateFromSelector,
                hasPostDetail: !!postDetailStateFromSelector?.postDetail,
                postTitle: postDetailStateFromSelector?.postDetail?.postTitle?.substring(0, 50)
            },
            fromStore: {
                hasState: !!storeState,
                hasPostDetail: !!storeState?.postDetail,
                postTitle: storeState?.postDetail?.postTitle?.substring(0, 50)
            },
            finalState: {
                hasState: !!postDetailState,
                hasPostDetail: !!postDetail,
                postTitle: postDetail?.postTitle?.substring(0, 50),
                postDetailKeys: postDetail ? Object.keys(postDetail) : []
            }
        });
        console.log('[SSR] PostDetailPage - Full postDetailState:', {
            hasState: !!postDetailState,
            stateKeys: postDetailState ? Object.keys(postDetailState) : [],
            hasPostDetail: !!postDetail,
            postDetailType: typeof postDetail,
            postDetailKeys: postDetail ? Object.keys(postDetail) : [],
            postDetailValue: postDetail,
            // Log the actual values that matter for metadata
            postTitle: postDetail?.postTitle,
            postDescription: postDetail?.postDescription,
            postThumbnail: postDetail?.postThumbnail,
            postId: postDetail?.id
        });

        // Additional diagnostic: Check if postDetailState structure matches expected format
        if (postDetailState) {
            console.log('[SSR] PostDetailPage - postDetailState structure:', {
                hasError: 'error' in postDetailState,
                hasPostDetailKey: 'postDetail' in postDetailState,
                postDetailStateKeys: Object.keys(postDetailState),
                postDetailStateStringified: JSON.stringify(postDetailState).substring(0, 200)
            });
        } else {
            console.error('[SSR] PostDetailPage - CRITICAL: postDetailState is null/undefined from selector!');
            console.error('[SSR] PostDetailPage - This means the Redux store does not have postDetail slice initialized');
        }
    }

    // On server, if data exists from loadData, consider it loaded immediately
    // On client, check if data is already loaded from SSR hydration
    // Check if postDetail exists and matches the requested ID (compare as strings to handle type mismatches)
    const postDetailId = postDetail?.id ? String(postDetail.id) : null;
    const requestedId = id ? String(id) : null;
    const hasPostData = postDetail && postDetailId === requestedId && (postDetail.id || postDetail.postTitle || postDetail.postContent);
    const canonicalDetailPath =
        isValidId && hasPostData ? buildBlogDetailPath(id, postDetail?.postTitle || postDetail?.postContent || id) : location.pathname;
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const pathname = location.pathname;
    const pageUrl = `${baseUrl}${canonicalDetailPath}`;

    // Initialize loadComplate state - on server, if we have data, consider it loaded
    const [loadComplate, setLoadComplate] = useState(hasPostData);

    // On server, ALWAYS render if postDetail exists - loadData ensures data is loaded before render
    // On client, use loadComplate state which is set by useEffect
    // CRITICAL: On server, we MUST render the HTML content for SEO and URL previews
    const shouldRenderContent = isServer
        ? !!postDetail // On server: render if postDetail exists (no other checks needed)
        : loadComplate && !!postDetail; // On client: render when loadComplate is true AND postDetail exists

    // Debug logging on server
    if (isServer && isDevelopment) {
        console.log('[SSR] PostDetailPage render - Server side:', {
            hasPostDetail: !!postDetail,
            postDetailId: postDetail?.id,
            postDetailTitle: postDetail?.postTitle,
            requestedId: id,
            hasPostTitle: !!postDetail?.postTitle,
            hasPostContent: !!postDetail?.postContent,
            hasPostDescription: !!postDetail?.postDescription,
            hasPostThumbnail: !!postDetail?.postThumbnail,
            willRender: hasPostData,
            shouldRenderContent: shouldRenderContent,
            loadComplate: loadComplate,
            postDetailKeys: postDetail ? Object.keys(postDetail) : []
        });

        // Critical check: if we're on the server and don't have post data, this is a problem
        // The loadData function should have loaded it before rendering
        if (!postDetail || !postDetail.postTitle) {
            console.error('[SSR] PostDetailPage render - CRITICAL: No post data available on server!');
            console.error('[SSR] PostDetailPage render - This means loadData either failed or did not complete before render');
            console.error('[SSR] PostDetailPage render - Article content will NOT be rendered in HTML');
            console.error('[SSR] PostDetailPage render - URL previews will show fallback/empty content');
            console.error('[SSR] PostDetailPage render - Requested ID:', id);
        } else {
            console.log('[SSR] PostDetailPage render - SUCCESS: Post data available, article will be rendered');
            console.log('[SSR] PostDetailPage render - shouldRenderContent:', shouldRenderContent);
            console.log('[SSR] PostDetailPage render - postDetail keys:', Object.keys(postDetail));
            console.log('[SSR] PostDetailPage render - has postTitle:', !!postDetail.postTitle);
            console.log('[SSR] PostDetailPage render - has postContent:', !!postDetail.postContent);
            console.log('[SSR] PostDetailPage render - WILL RENDER ARTICLE:', shouldRenderContent && !!postDetail);
        }
    }

    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') return;

        // Load categories in background (needed for category chips, but not blocking)
        if (!categoryList || categoryList.length === 0) {
            dispatch(getCategories({}));
        }
    }, [dispatch, categoryList]);

    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') return;
        if (!isValidId) return;
        if (isNotFoundFromStore) return;

        // Load post directly if we have an ID and don't already have the correct post loaded
        // Compare IDs as strings to handle type mismatches
        const currentPostId = postDetail?.id ? String(postDetail.id) : null;
        const requestedId = id ? String(id) : null;

        if (id && (!postDetail || currentPostId !== requestedId)) {
            dispatch(
                clearPostInStore({}, () => {
                    dispatch(getBlogPost(id));
                })
            );
        }
    }, [dispatch, id, isNotFoundFromStore, isValidId, postDetail]);

    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') return;

        // Set loadComplate when postDetail is loaded and matches the requested ID
        // Don't wait for categories - they're not required for displaying the post
        // Compare IDs as strings to handle type mismatches
        const currentPostId = postDetail?.id ? String(postDetail.id) : null;
        const requestedId = id ? String(id) : null;

        if (postDetail && currentPostId === requestedId) {
            setLoadComplate(true);
        } else if (postDetail && currentPostId !== requestedId) {
            // If we have a different post loaded, reset loadComplate
            setLoadComplate(false);
        }
    }, [postDetail, id]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!isValidId) return;
        if (!hasPostData) return;

        const nextPath = buildBlogDetailPath(id, postDetail?.postTitle || postDetail?.postContent || id);
        if (location.pathname !== nextPath) {
            navigate(nextPath, { replace: true });
        }
    }, [hasPostData, id, isValidId, location.pathname, navigate, postDetail?.postContent, postDetail?.postTitle]);

    // SSR fallback: if detail route has no renderable post payload, render 404 instead of a blank "BLOG" shell.
    const isMissingServerPostPayload = isServer && !hasRenderablePostData;
    const shouldRenderNotFound =
        !isValidId ||
        isNotFoundFromStore ||
        (postDetail && !hasRenderablePostData) ||
        isMissingServerPostPayload ||
        (hasRequestError && !hasRenderablePostData);
    if (shouldRenderNotFound) {
        return <PageNotFound />;
    }

    const getColor = (targetCategory, type) => {
        let color = '#FFFFFF';
        if (categoryList && categoryList.length > 0) {
            categoryList.forEach((category, index) => {
                if (category.name === targetCategory) {
                    if (type === 'bg') {
                        color = category.backgroundColor;
                    } else if (type === 'text') {
                        color = category.textColor;
                    }
                }
            });
        }
        return color;
    };

    // Helper function to strip HTML tags and get plain text from postContent
    const stripHtmlTags = (html) => {
        if (!html) return '';
        // Remove HTML tags, decode HTML entities, and clean up whitespace
        const text = html
            .replace(/<[^>]*>/g, ' ') // Remove HTML tags
            .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
            .replace(/&amp;/g, '&') // Decode &amp;
            .replace(/&lt;/g, '<') // Decode &lt;
            .replace(/&gt;/g, '>') // Decode &gt;
            .replace(/&quot;/g, '"') // Decode &quot;
            .replace(/&#39;/g, "'") // Decode &#39;
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim(); // Remove leading/trailing whitespace
        return text;
    };

    // Extract blog content image URLs from HTML (<img src="/img/blog/content/...">) for BlogPosting schema image enrichment.
    const extractBlogContentImageUrls = (html, origin) => {
        if (!html || typeof html !== 'string' || !origin) return [];

        const urls = [];
        const seen = new Set();

        const pushIfValid = (src) => {
            if (!src || typeof src !== 'string') return;
            const normalized = src.trim();
            if (!normalized.startsWith('/img/blog/content/')) return;

            const absoluteUrl = `${origin}${normalized}`;
            if (seen.has(absoluteUrl)) return;
            seen.add(absoluteUrl);
            urls.push(absoluteUrl);
        };

        // Match quoted src attribute values: src="..." / src='...'
        const quotedSrcRegex = /<img\b[^>]*\bsrc\s*=\s*(['"])(.*?)\1/gi;
        let quotedMatch = quotedSrcRegex.exec(html);
        while (quotedMatch !== null) {
            pushIfValid(quotedMatch[2]);
            quotedMatch = quotedSrcRegex.exec(html);
        }

        // Match unquoted src values: src=/img/blog/content/...
        const unquotedSrcRegex = /<img\b[^>]*\bsrc\s*=\s*([^\s"'<>`]+)/gi;
        let unquotedMatch = unquotedSrcRegex.exec(html);
        while (unquotedMatch !== null) {
            pushIfValid(unquotedMatch[1]);
            unquotedMatch = unquotedSrcRegex.exec(html);
        }

        return urls;
    };

    /** YYYY-MM-DD for Article JSON-LD and article:* meta tags */
    const toArticleSchemaDateOnly = (value) => {
        if (value == null || value === '') return undefined;
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return undefined;
        return d.toISOString().slice(0, 10);
    };

    /**
     * Legacy summaries use "publishDate - rest of summary"; use the leading segment as the public publish date.
     */
    const parseLegacyDescriptionPublishDate = (postDescription, stripHtml) => {
        if (!postDescription || typeof postDescription !== 'string') return undefined;
        const plain = stripHtml(postDescription);
        const first = plain.split(' - ')[0]?.trim();
        if (!first) return undefined;
        if (/^\d{4}-\d{2}-\d{2}$/.test(first)) return first;
        const ms = Date.parse(first);
        if (Number.isNaN(ms)) return undefined;
        return new Date(ms).toISOString().slice(0, 10);
    };

    // Generate SEO metadata
    // Use postDetail data if available, otherwise use fallbacks
    // For SSR, ensure we always have valid meta tags even if data is still loading
    // CRITICAL: On server, postDetail should be available from loadData - if not, log error
    // CRITICAL: Always generate meta tags (even with fallbacks) so crawlers can see them
    const pageTitle = postDetail?.postTitle
        ? `${postDetail.postTitle} | Blog | Hotmob - Data-Centric Media Company`
        : 'Blog | Hotmob - Data-Centric Media Company';
    // Meta + WebPage description: quick takeaways first; Article JSON-LD description uses legacy postDescription (below)
    const quickTakeawaysPlain = postDetail?.postQuickTakeaways ? stripHtmlTags(postDetail.postQuickTakeaways) : '';
    const legacyDescriptionPlain = postDetail?.postDescription ? stripHtmlTags(postDetail.postDescription) : '';
    const bodyPlainFallback = postDetail?.postContent ? stripHtmlTags(postDetail.postContent) : '';
    const rawDescription = quickTakeawaysPlain || legacyDescriptionPlain || bodyPlainFallback || GEO_SITE_TAGLINE;
    const pageDescription = rawDescription.length > 160 ? `${rawDescription.substring(0, 157)}...` : rawDescription;
    // Article schema description: original post summary first; abstract field carries quick takeaways
    const articleSchemaDescriptionRaw = legacyDescriptionPlain || quickTakeawaysPlain || bodyPlainFallback || GEO_SITE_TAGLINE;
    const articleSchemaDescription =
        articleSchemaDescriptionRaw.length > 160 ? `${articleSchemaDescriptionRaw.substring(0, 157)}...` : articleSchemaDescriptionRaw;
    // JSON-LD abstract: full summary text for GEO (aligned with on-page itemProp="abstract")
    const articleAbstractPlain =
        quickTakeawaysPlain && quickTakeawaysPlain.length > 5000
            ? `${quickTakeawaysPlain.slice(0, 4997)}...`
            : quickTakeawaysPlain || undefined;

    // CRITICAL: On server, if we don't have postDetail but have a valid ID, this is an error
    // This means loadData didn't complete or data wasn't stored correctly
    if (isServer && isDevelopment && isValidId && !postDetail) {
        console.error('[SSR] PostDetailPage - CRITICAL: Valid ID but no postDetail in store!');
        console.error('[SSR] PostDetailPage - ID:', id);
        console.error('[SSR] PostDetailPage - postDetailState:', postDetailState);
        console.error('[SSR] PostDetailPage - This will cause meta tags to use fallback values');
        console.error('[SSR] PostDetailPage - URL previews will NOT show post data');
    }

    // Extract plain text from postContent for URL previews (longer than meta description)
    const postContentText = postDetail?.postContent ? stripHtmlTags(postDetail.postContent) : rawDescription;
    // For URL previews, use a longer version (up to 500 chars) of the post content
    // This ensures social media previews show meaningful content
    const ogDescription = postContentText.length > 500 ? `${postContentText.substring(0, 497)}...` : postContentText;

    // Ensure image URL is absolute for Open Graph (required for social media previews)
    // Use post thumbnail if available, otherwise use a default logo/image
    // CRITICAL: og:image must be an absolute URL for social media crawlers
    // WhatsApp and other chat apps require the image to be publicly accessible
    let pageImage = postDetail?.postThumbnail || undefined;
    let imageUrlValid = false;

    // Helper: transform blog thumbnail to use 500px variant, e.g.
    // '/img/blog/thumbnail/1736907599990.jpg' -> '/img/blog/thumbnail/1736907599990_500.jpg'
    const to500Variant = (urlPath) => {
        if (!urlPath || typeof urlPath !== 'string') return urlPath;

        // Only adjust known blog thumbnail paths
        if (!urlPath.includes('/img/blog/thumbnail/')) return urlPath;

        // Skip if it already has _500 before the extension
        if (/_500\.(jpg|jpeg|png|webp)(\?|$)/i.test(urlPath)) return urlPath;

        // Insert _500 before the file extension
        return urlPath.replace(/\.(jpg|jpeg|png|webp)(\?|$)/i, '_500.$1$2');
    };

    // Process and validate the image URL
    if (pageImage) {
        // Trim whitespace
        pageImage = pageImage.trim();

        // Skip if empty after trim
        if (!pageImage) {
            pageImage = undefined;
        } else {
            // Prefer 500px variant for known blog thumbnails **before** making URL absolute
            pageImage = to500Variant(pageImage);

            // Handle different URL formats
            if (!pageImage.startsWith('http://') && !pageImage.startsWith('https://')) {
                // Convert relative URL to absolute
                if (pageImage.startsWith('/')) {
                    pageImage = `${baseUrl}${pageImage}`;
                } else if (pageImage.startsWith('//')) {
                    // Protocol-relative URL (//example.com/image.jpg)
                    pageImage = baseUrl.startsWith('https://') ? `https:${pageImage}` : `http:${pageImage}`;
                } else {
                    // Relative path without leading slash
                    pageImage = `${baseUrl}/${pageImage}`;
                }
            }

            // Validate URL format
            try {
                const urlObj = new URL(pageImage);
                // Ensure HTTPS for better compatibility (if baseUrl is HTTPS)
                if (baseUrl.startsWith('https://') && urlObj.protocol === 'http:') {
                    urlObj.protocol = 'https:';
                    pageImage = urlObj.toString();
                }

                // Check if URL looks valid (has hostname)
                if (urlObj.hostname && urlObj.hostname.length > 0) {
                    imageUrlValid = true;
                } else {
                    if (isServer && isDevelopment) {
                        console.warn('[SSR] PostDetailPage - Image URL missing hostname:', pageImage);
                    }
                    imageUrlValid = false;
                }
            } catch (e) {
                // If URL parsing fails, the URL is invalid
                if (isServer && isDevelopment) {
                    console.error('[SSR] PostDetailPage - Invalid image URL format:', pageImage);
                    console.error('[SSR] PostDetailPage - URL parse error:', e.message);
                }
                imageUrlValid = false;
                pageImage = undefined; // Don't use invalid URL
            }
        }
    }

    // Fallback to default image if no valid image is available
    // CRITICAL: Use a real image file (not SVG) for better WhatsApp compatibility
    // WhatsApp doesn't support SVG images well - use PNG or JPEG
    if (!pageImage || !imageUrlValid) {
        // Use a proper image file that exists and is accessible
        // Try common locations for a default blog/logo image
        const fallbackImages = [
            `${baseUrl}/img/static/icon/hotmob_logo_3X.png`, // PNG version
            `${baseUrl}/img/static/icon/hotmob_2X.jpg`, // JPEG version
            `${baseUrl}/img/static/icon/favicon-32x32.png` // Last resort - favicon
        ];

        // Use the first fallback (you can customize this based on what exists)
        pageImage = fallbackImages[0];

        if (isServer && isDevelopment) {
            if (!postDetail?.postThumbnail) {
                console.warn('[SSR] PostDetailPage - No postThumbnail in API data, using fallback image');
            } else {
                console.warn('[SSR] PostDetailPage - postThumbnail URL is invalid, using fallback image');
                console.warn('[SSR] PostDetailPage - Original postThumbnail value:', postDetail.postThumbnail);
            }
            console.warn('[SSR] PostDetailPage - Using fallback image:', pageImage);
        }
    }

    // Detect image type from URL extension for better meta tags
    // CRITICAL: WhatsApp and most chat apps prefer JPEG or PNG over SVG
    const getImageType = (url) => {
        if (!url) return 'image/jpeg';
        const lowerUrl = url.toLowerCase();
        // Check for file extension (more reliable than just checking if string contains)
        const extensionMatch = lowerUrl.match(/\.(jpg|jpeg|png|webp|svg|gif)(\?|$)/);
        if (extensionMatch) {
            const ext = extensionMatch[1];
            if (ext === 'png') return 'image/png';
            if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
            if (ext === 'webp') return 'image/webp';
            if (ext === 'svg') return 'image/svg+xml';
            if (ext === 'gif') return 'image/gif';
        }
        // Fallback: check if URL contains extension (less reliable)
        if (lowerUrl.includes('.png')) return 'image/png';
        if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg')) return 'image/jpeg';
        if (lowerUrl.includes('.webp')) return 'image/webp';
        if (lowerUrl.includes('.svg')) return 'image/svg+xml';
        if (lowerUrl.includes('.gif')) return 'image/gif';
        // Default to JPEG (best compatibility with WhatsApp and chat apps)
        return 'image/jpeg';
    };

    const imageType = getImageType(pageImage);

    // Warn if using SVG (not well supported by WhatsApp)
    if (isServer && isDevelopment && imageType === 'image/svg+xml') {
        console.warn('[SSR] PostDetailPage - WARNING: Using SVG image which may not work in WhatsApp');
        console.warn('[SSR] PostDetailPage - Consider using JPEG or PNG for better compatibility');
    }

    // Article-specific metadata (publish date from legacy "date - summary" when present; else DB created)
    const articlePublishedTime =
        parseLegacyDescriptionPublishDate(postDetail?.postDescription, stripHtmlTags) ?? toArticleSchemaDateOnly(postDetail?.createdDate);
    const articleModifiedTime = toArticleSchemaDateOnly(postDetail?.lastModifiedDate || postDetail?.createdDate);
    const contentImageUrls = extractBlogContentImageUrls(postDetail?.postContent, baseUrl);
    const primaryThumbnail = postDetail?.postThumbnail ? `${baseUrl}${postDetail.postThumbnail}` : undefined;
    const blogPostingImageList = Array.from(new Set([primaryThumbnail, ...contentImageUrls].filter(Boolean)));

    // Author information for article schema
    const articleAuthor = postDetail?.author || 'Hotmob';

    // Debug logging for SEO metadata generation
    if (isServer && isDevelopment) {
        const isUsingFallback = !postDetail || !postDetail.postTitle;
        console.log('[SSR] PostDetailPage SEO metadata generation:', {
            hasPostDetail: !!postDetail,
            postDetailTitle: postDetail?.postTitle,
            postDetailDescription: postDetail?.postDescription,
            postDetailThumbnail: postDetail?.postThumbnail,
            hasPostContent: !!postDetail?.postContent,
            postContentLength: postDetail?.postContent ? postDetail.postContent.length : 0,
            postContentTextLength: postContentText ? postContentText.length : 0,
            pageTitle,
            pageDescription: pageDescription.substring(0, 50),
            ogDescription: ogDescription.substring(0, 50),
            pageImage: pageImage ? pageImage : 'none',
            pageImageType: imageType,
            pageImageIsHttps: pageImage ? pageImage.startsWith('https://') : false,
            pageImageIsValid: imageUrlValid,
            originalPostThumbnail: postDetail?.postThumbnail || 'none',
            pageUrl,
            isImageAbsolute: pageImage ? pageImage.startsWith('http://') || pageImage.startsWith('https://') : false,
            imageUrlValidation: {
                hasPostThumbnail: !!postDetail?.postThumbnail,
                isAbsolute: pageImage ? pageImage.startsWith('http://') || pageImage.startsWith('https://') : false,
                isValidUrl: imageUrlValid,
                finalImageUrl: pageImage
            },
            postDetailKeys: postDetail ? Object.keys(postDetail) : [],
            willRenderMetaTags: true,
            metaTitleLength: pageTitle.length,
            metaDescriptionLength: pageDescription.length,
            ogDescriptionLength: ogDescription.length,
            isUsingFallback: isUsingFallback,
            warning: isUsingFallback ? 'USING FALLBACK VALUES - API DATA NOT AVAILABLE' : 'Using API data'
        });

        if (isUsingFallback) {
            console.error('[SSR] CRITICAL: Metadata is using FALLBACK values instead of API data!');
            console.error('[SSR] This means postDetail is null/undefined when generating metadata');
            console.error('[SSR] URL previews will show generic content instead of blog post content');
            console.error('[SSR] Check if loadData completed successfully and data is in Redux store');
        } else {
            console.log('[SSR] SUCCESS: Metadata is using API data - URL previews will show correct content');
        }
    }

    // Generate structured data for SEO
    const hasPageTitle = typeof pageTitle === 'string' && pageTitle.trim().length > 0;
    const safePostTitle = postDetail?.postTitle || pageTitle || 'Blog | Hotmob - Data-Centric Media Company';
    const structuredData = hasPageTitle
        ? buildSchemaGraph([
              generateWebPageSchema({
                  title: pageTitle,
                  description: pageDescription,
                  path: pathname,
                  about: 'organization',
                  mainEntity: `${pathname}/#article`,
                  image: {
                      path: postDetail?.postThumbnail,
                      width: null,
                      height: null
                  }
              }),
              generateBlogPostingSchema({
                  title: safePostTitle,
                  description: articleSchemaDescription,
                  ...(articleAbstractPlain && { abstract: articleAbstractPlain }),
                  image: blogPostingImageList,
                  datePublished: articlePublishedTime,
                  dateModified: articleModifiedTime || articlePublishedTime,
                  path: pathname,
                  // GEO: cssSelector targets must exist in the article (see #post-title, #post-abstract, #post-content)
                  speakable: quickTakeawaysPlain ? ['#post-title', '#post-abstract', '#post-content'] : ['#post-title', '#post-content']
              }),
              generateBreadcrumbSchema({
                  pagePath: pathname,
                  items: [
                      { name: 'Home', url: '/' },
                      { name: 'Blog', url: buildBlogListPath('all', 1) },
                      { name: safePostTitle, url: pathname }
                  ]
              })
          ])
        : null;

    return (
        <Container
            sx={(t) => ({
                width: '100%',
                maxWidth: '1280px',
                margin: '0 auto !important',
                padding: '80px 40px !important',
                [t.breakpoints.down('md')]: {
                    padding: '80px 24px !important'
                }
            })}
        >
            {/* CRITICAL: Always render Helmet with meta tags - even if postDetail is null
                This ensures crawlers and social media platforms can always see meta tags
                The tags will use fallback values if postDetail is not available */}
            <Helmet>
                <html lang="en" />
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={pageUrl} />

                {/* Open Graph / Facebook - Required for URL previews */}
                {/* CRITICAL: These tags are required for WhatsApp, Facebook, LinkedIn, and other chat apps */}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={pageImage} />
                <meta property="og:image:secure_url" content={pageImage} />
                {/* Image dimensions - WhatsApp prefers 1200x630 (1.91:1 ratio) but accepts various sizes */}
                {/* 
                    CRITICAL: If testing tool says "ratio isn't optimal":
                    1. Ensure your blog post thumbnails are actually 1200x630px (1.91:1 aspect ratio)
                    2. Or remove width/height tags below to let platforms auto-detect dimensions
                    3. Note: Having incorrect dimensions is worse than omitting them
                */}
                {/* 
                    OPTIONAL: Uncomment these only if your images are consistently 1200x630px.
                    If your images vary in size, it's better to omit these tags.
                */}
                {/* <meta property="og:image:width" content="1200" /> */}
                {/* <meta property="og:image:height" content="630" /> */}
                <meta property="og:image:type" content={imageType} />
                <meta property="og:image:alt" content={postDetail?.postTitle || 'Blog post image'} />
                <meta property="og:site_name" content={GEO_OG_SITE_NAME} />
                <meta property="og:locale" content="en_US" />
                {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
                {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
                {articleAuthor && <meta property="article:author" content={articleAuthor} />}
                {postDetail?.postCategory && postDetail.postCategory.length > 0 && (
                    <>
                        {postDetail.postCategory.map((category) => (
                            <meta key={`article-tag-${category}`} property="article:tag" content={category} />
                        ))}
                    </>
                )}

                {/* Twitter Card - Required for Twitter URL previews */}
                {/* summary_large_image shows a large image preview (recommended for blog posts) */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={ogDescription} />
                <meta name="twitter:image" content={pageImage} />
                <meta name="twitter:image:alt" content={postDetail?.postTitle || 'Blog post image'} />
                <meta name="twitter:site" content="@hotmob" />

                {/* Robots - Tell search engines to index and follow */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />

                {/* Preload critical image for faster rendering */}
                {pageImage && <link rel="preload" as="image" href={pageImage} fetchPriority="high" />}

                {/* Structured Data - Helps search engines understand the content */}
                {structuredData && renderMultipleStructuredData([structuredData])}
            </Helmet>
            <Typography
                sx={(t) => ({
                    color: '#1C1C1C',
                    fontSize: '3.75rem',
                    fontWeight: 800,
                    fontFamily: "'Heebo Variable', Heebo, sans-serif",
                    marginTop: 0,
                    marginBottom: '60px',
                    lineHeight: 1.167,
                    letterSpacing: '-0.01562em',
                    [t.breakpoints.down(959.95)]: {
                        fontSize: '1.5rem',
                        marginBottom: '20px'
                    }
                })}
            >
                <Box component="span">BLO</Box>
                <Box component="span" sx={{ color: '#E15C12' }}>
                    G
                </Box>
            </Typography>
            {/* CRITICAL FOR SSR: Render article content in HTML for SEO and URL previews
                On server: Always render if postDetail exists (loadData ensures it's loaded)
                On client: Render when loadComplate is true
                This HTML must be in the page source for search engines and social media previews
                The article content will be visible in "view page source" for SEO purposes
                CRITICAL: Search engine crawlers need to see the actual content in the HTML
            */}
            {shouldRenderContent && postDetail && postDetail.postContent ? (
                <article itemScope itemType="https://schema.org/Article">
                    <Grid container spacing={2} alignItems="center">
                        {postDetail.postThumbnail && (
                            <Grid item xs={12}>
                                <Box
                                    id="post-thumbnail"
                                    component="img"
                                    alt={`${postDetail.postTitle} - thumbnail` || 'Blog post image'}
                                    src={`${process.env.REACT_APP_BASE_URL}${postDetail.postThumbnail}`}
                                    sx={{ width: '100%', height: 'auto', borderRadius: '16px' }}
                                />
                            </Grid>
                        )}
                        {postDetail.postCategory && postDetail.postCategory.length > 0 && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex' }}>
                                    {postDetail.postCategory.map((category, index) => (
                                        <Chip
                                            key={`category-${category}-${index}`}
                                            index={index}
                                            label={category}
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                padding: '0 1rem',
                                                marginRight: '1rem',
                                                background: getColor(category, 'bg'),
                                                color: getColor(category, 'text')
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography
                                id="post-title"
                                component="h1"
                                variant="h1"
                                itemProp="headline"
                                sx={(t) => ({
                                    color: '#1C1C1C',
                                    fontSize: '3.125rem',
                                    fontWeight: 800,
                                    fontFamily: "'Heebo Variable', Heebo, sans-serif",
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.00833em',
                                    marginTop: 0,
                                    marginBottom: '1.5rem',
                                    [t.breakpoints.down(1099.95)]: { marginBottom: '1rem' },
                                    [t.breakpoints.down(959.95)]: { fontSize: '1.875rem' }
                                })}
                            >
                                {postDetail.postTitle}
                            </Typography>
                        </Grid>
                        {quickTakeawaysPlain && (
                            <Grid item xs={12}>
                                <Box
                                    component="section"
                                    id="post-abstract"
                                    sx={{
                                        padding: '32px 80px !important',
                                        backgroundColor: '#FBFBFB',
                                        borderRadius: '16px',
                                        marginBottom: '24px',
                                        [theme.breakpoints.down(600)]: {
                                            padding: '16px !important'
                                        }
                                    }}
                                    itemProp="abstract"
                                >
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            fontStyle: 'italic',
                                            color: '#1C1C1C',
                                            marginBottom: '20px',
                                            [theme.breakpoints.down(600)]: {
                                                fontSize: '16px',
                                                marginBottom: '16px'
                                            }
                                        }}
                                    >
                                        Quick takeaways
                                    </Typography>
                                    <ThemeProvider theme={contentTheme}>
                                        <Box className="quick-takeaways-content">
                                            <CssBaseline />
                                            {parse(postDetail.postQuickTakeaways.replaceAll('&nbsp;', ' '))}
                                        </Box>
                                    </ThemeProvider>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <div id="post-content" itemProp="articleBody">
                                <ThemeProvider theme={contentTheme}>
                                    <CssBaseline />
                                    {parse(postDetail.postContent.replaceAll('&nbsp;', ' '))}
                                </ThemeProvider>
                            </div>
                        </Grid>
                    </Grid>
                </article>
            ) : null}
        </Container>
    );
};

export default PostDetailPage;

// Server-side data preloading helper
export async function loadData(store, params = {}) {
    try {
        // Validate environment variable on server - only require in development
        // In production/UAT, allow API calls to proceed (API is proxied via nginx)
        const isDevelopment = process.env.NODE_ENV === 'development';
        const shouldLog = isDevelopment;

        const log = (...args) => {
            if (shouldLog) {
                console.log(...args);
            }
        };

        const warn = (...args) => {
            if (shouldLog) {
                console.warn(...args);
            }
        };

        const error = (...args) => {
            if (shouldLog) {
                console.error(...args);
            }
        };

        log('[SSR] PostDetailPage.loadData called with params:', params);
        if (typeof window === 'undefined' && isDevelopment && !process.env.REACT_APP_API_SERVER) {
            error('[SSR] PostDetailPage.loadData - REACT_APP_API_SERVER is not set on server');
            error('[SSR] PostDetailPage.loadData - Cannot make API calls without API server URL');
            error('[SSR] PostDetailPage.loadData - Skipping data loading, page will render without data');
            // Don't throw - allow page to render even if API is not configured
            return;
        }

        // Extract and validate ID from params
        const id = params && params.id ? params.id : null;
        log('[SSR] PostDetailPage.loadData - Received params:', JSON.stringify(params));
        log('[SSR] PostDetailPage.loadData - Extracted ID from params:', id, 'Type:', typeof id);

        // Validate ID - check for null, undefined, empty string, or the string 'null'
        // If invalid, try to extract from the URL as a fallback (for edge cases)
        if (!id || id === 'null' || id === 'undefined' || (typeof id === 'string' && id.trim() === '')) {
            error('[SSR] PostDetailPage.loadData - CRITICAL: No valid ID provided in params');
            error('[SSR] PostDetailPage.loadData - Params object:', JSON.stringify(params));
            error('[SSR] PostDetailPage.loadData - ID value:', id, 'Type:', typeof id);

            // Try to extract ID from URL as fallback (if params extraction failed)
            // This handles edge cases where route matching might not work correctly
            if (params && typeof params === 'object' && Object.keys(params).length > 0) {
                // Check if there's a URL in the params or if we can get it from the store/context
                // For now, we'll just log and return - the URL should be properly formatted
                error('[SSR] PostDetailPage.loadData - Cannot extract valid ID from route params');
            }

            error('[SSR] PostDetailPage.loadData - This will cause the page to render without blog post data');
            error('[SSR] PostDetailPage.loadData - Check the URL - it should be /blog/detail/{actual-id}, not /blog/detail/null');
            error('[SSR] PostDetailPage.loadData - If the URL is correct, check route configuration and parameter extraction');
            store.dispatch({ type: 'postDetail/setNotFound', payload: true });
            store.dispatch({
                type: 'postDetail/hasError',
                payload: { status: 404, message: 'Invalid blog post ID' }
            });
            // Don't throw - allow page to render even without ID, but log the error
            return;
        }

        // Additional validation: ensure ID is not just whitespace
        if (typeof id === 'string' && id.trim() === '') {
            error('[SSR] PostDetailPage.loadData - ID is empty string after trim');
            store.dispatch({ type: 'postDetail/setNotFound', payload: true });
            store.dispatch({
                type: 'postDetail/hasError',
                payload: { status: 404, message: 'Invalid blog post ID' }
            });
            return;
        }

        try {
            await store.dispatch(clearStore());
        } catch (error) {
            warn('[SSR] PostDetailPage.loadData - Error clearing store:', error?.message || error);
            // Continue even if clearing store fails
        }

        try {
            await store.dispatch(getCategories());
        } catch (error) {
            error('[SSR] PostDetailPage.loadData - Error loading categories:', error?.message || error);
            error('[SSR] PostDetailPage.loadData - Error details:', {
                message: error?.message,
                response: error?.response?.status,
                responseData: error?.response?.data,
                baseURL: typeof window === 'undefined' ? process.env.REACT_APP_API_SERVER : 'client'
            });
            // Continue to try loading post even if categories fail
        }

        try {
            // Clear the store first
            store.dispatch(clearPostInStore());

            // Load the blog post data and wait for it to complete
            // The dispatch returns the result of the thunk, which is the response.data
            const result = await store.dispatch(getBlogPost(id));
            log('[SSR] PostDetailPage.loadData - Post loaded from API:', {
                hasResult: !!result,
                resultType: typeof result,
                resultKeys: result ? Object.keys(result) : [],
                postId: result?.id,
                postTitle: result?.postTitle?.substring(0, 50),
                hasPostTitle: !!result?.postTitle,
                hasPostDescription: !!result?.postDescription,
                hasPostThumbnail: !!result?.postThumbnail
            });

            // CRITICAL: Wait a microtask to ensure Redux state is fully updated
            // This ensures the store state is synchronized before component render
            // Redux Toolkit updates synchronously, but this ensures any async side effects complete
            await new Promise((resolve) => {
                if (typeof setImmediate !== 'undefined') {
                    setImmediate(resolve);
                } else {
                    // Fallback for environments without setImmediate
                    setTimeout(resolve, 0);
                }
            });

            // Verify data is in store after loading
            // Redux Toolkit updates state synchronously, but we verify after microtask
            const state = store.getState();
            const postDetailData = state.postDetail?.postDetail;
            log('[SSR] PostDetailPage.loadData - Store verification:', {
                hasPostDetailSlice: !!state.postDetail,
                hasPostDetail: !!postDetailData,
                postDetailId: postDetailData?.id,
                postDetailTitle: postDetailData?.postTitle?.substring(0, 50),
                postDetailDescription: postDetailData?.postDescription?.substring(0, 50),
                postDetailThumbnail: postDetailData?.postThumbnail ? postDetailData.postThumbnail.substring(0, 50) : 'none',
                matchesRequestedId: postDetailData?.id === id || String(postDetailData?.id) === String(id),
                postDetailKeys: postDetailData ? Object.keys(postDetailData) : []
            });

            // Critical check: if data is not in store after loading, this is a problem
            if (!postDetailData || !postDetailData.postTitle) {
                error('[SSR] PostDetailPage.loadData - CRITICAL ERROR: Data not in store after loading!');
                error('[SSR] PostDetailPage.loadData - This will cause URL previews to show empty/fallback content!');
                error('[SSR] PostDetailPage.loadData - API result:', result ? JSON.stringify(result, null, 2) : 'null/undefined');
                error('[SSR] PostDetailPage.loadData - Store state:', JSON.stringify(state.postDetail, null, 2));

                // If API returned data but store doesn't have it, retry the dispatch
                // This handles edge cases where the store update didn't complete
                if (result && result.postTitle) {
                    warn('[SSR] PostDetailPage.loadData - WARNING: API data exists but not in store, retrying dispatch...');
                    try {
                        // Retry the dispatch to ensure store is updated
                        await store.dispatch(getBlogPost(id));

                        // Verify again after retry
                        const stateAfterRetry = store.getState();
                        const postDetailAfterRetry = stateAfterRetry.postDetail?.postDetail;
                        if (postDetailAfterRetry && postDetailAfterRetry.postTitle) {
                            log('[SSR] PostDetailPage.loadData - SUCCESS: Data in store after retry');
                        } else {
                            error('[SSR] PostDetailPage.loadData - ERROR: Retry failed, data still not in store!');
                        }
                    } catch (retryError) {
                        error('[SSR] PostDetailPage.loadData - ERROR: Retry dispatch failed:', retryError?.message || retryError);
                    }
                }
            } else {
                // Final verification that we have the required fields for SEO
                if (!postDetailData.postQuickTakeaways && !postDetailData.postDescription) {
                    warn(
                        '[SSR] PostDetailPage.loadData - WARNING: Missing postQuickTakeaways and postDescription (meta/summary weaker for SEO)'
                    );
                }
                if (!postDetailData.postThumbnail) {
                    warn('[SSR] PostDetailPage.loadData - WARNING: Missing postThumbnail field');
                }
                log('[SSR] PostDetailPage.loadData - SUCCESS: All required data loaded for SEO');
            }
        } catch (error) {
            error('[SSR] PostDetailPage.loadData - Error loading blog post:', error?.message || error);
            error('[SSR] PostDetailPage.loadData - Error details:', {
                message: error?.message,
                status: error?.status || error?.response?.status,
                responseData: error?.response?.data,
                responseStatusText: error?.response?.statusText,
                baseURL: typeof window === 'undefined' ? process.env.REACT_APP_API_SERVER : 'client',
                url: `/api/post/detail/${id}`
            });
            const responseStatus = error?.status || error?.response?.status || null;
            store.dispatch({ type: 'postDetail/setNotFound', payload: responseStatus === 404 });
            store.dispatch({
                type: 'postDetail/hasError',
                payload: {
                    status: responseStatus,
                    message: error?.message || error?.response?.data?.message || 'Failed to load blog post'
                }
            });
            // Don't throw - allow page to render even if data loading fails
            // This ensures the page structure is still rendered for SEO
        }
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.error('[SSR] PostDetailPage.loadData - Unexpected error:', error?.message || error);
            console.error('[SSR] PostDetailPage.loadData - Error stack:', error?.stack);
        }
        // Don't throw - allow page to render even if data loading fails
        // This ensures the page structure is still rendered for SEO
    }
}

// Attach loadData as a static method to the component for SSR
PostDetailPage.loadData = loadData;
